import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

import { SearchQuery, SearchQueryField } from 'modules/workflows/types';
import {
  isDataTypeSearchCombo,
  isGeoSearchField,
  isSearchQueryField,
} from 'modules/workflows/utils';
import { isMultiValueSearchField } from 'modules/workflows/utils/walkSearchQueries/walkSearchQueries.utils';
import { IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';

const isCoordinateValid = (coordinate: unknown) => {
  if (isNil(coordinate)) return false;
  if (isNumber(coordinate)) return true;
  if (isString(coordinate)) return !Number.isNaN(parseFloat(coordinate));
  return false;
};

const isFieldValid = (query: SearchQueryField) => {
  const comparator = query?.comparatorType;
  const hasField = !!query?.field;
  const hasComparator = !!comparator;

  // GeoSearchField requires a location and distance, and doesn't care about value
  if (isGeoSearchField(query)) {
    const { location, distance } = query;
    const hasLocation = isCoordinateValid(location?.lat) && isCoordinateValid(location?.lon);
    const hasDistance = isNumber(distance?.distance) && !!distance?.unit;
    return hasField && hasComparator && hasLocation && hasDistance;
  }

  // IsSet and IsNotSet comparators do not require a value
  if ([IS_SET, IS_NOT_SET].includes(comparator)) {
    return hasField && hasComparator;
  }

  if (isMultiValueSearchField(query)) {
    return hasField && hasComparator && query.values?.length > 0;
  }

  // All others require a value
  return hasField && hasComparator && !!query?.value;
};

/**
 * Are there any missing fields in a search query?
 */
export const isQueryIncomplete = (query: SearchQuery) => {
  if (isDataTypeSearchCombo(query) && !query.dataType) return true;
  if (isSearchQueryField(query) && !isFieldValid(query)) return true;

  return false;
};
